class GridFilter extends HTMLElement {
  constructor() {
    super();
    this.filterButton = this.querySelector('[data-filter-button]');
    this.filterBody = this.querySelector('[data-filter-body]');
    this.filterTextClosed = this.querySelector('[data-filter-text-closed]');
    this.filterTextOpen = this.querySelector('[data-filter-text-open]');
    this.filterForm = this.querySelector('#FacetFiltersForm');
    this.productGrid = document.querySelector('#product-grid');
    this.isOpen = false;
    this.abortController = null;

    this.init();
  }

  init() {
    // Toggle filter on button click
    this.filterButton.addEventListener('click', () => this.toggle());

    // Handle filter group expand/collapse
    this.querySelectorAll('[data-filter-toggle]').forEach(button => {
      button.addEventListener('click', (e) => this.toggleGroup(e.currentTarget));
    });

    // Handle click outside to close
    document.addEventListener('click', (e) => this.handleClickOutside(e));

    // Handle nav click to close
    const nav = document.querySelector('nav');
    if (nav) {
      nav.addEventListener('click', () => this.close());
    }

    // Handle filter form submission
    if (this.filterForm) {
      this.filterForm.addEventListener('submit', (e) => this.onSubmitForm(e));
    }

    // Handle label clicks using event delegation on filterBody (which doesn't get replaced)
    this.filterBody.addEventListener('click', (e) => {
      const item = e.target.closest('[data-filter-item]');
      if (!item) return;

      e.preventDefault();

      const input = item.querySelector('[data-filter-input]');
      if (!input) return;

      if (input.type === 'radio') {
        // Radio logic: single select with toggle
        const group = input.closest('[data-filter-group]');
        const wasActive = input.checked;

        // Uncheck all radios in the group first
        group.querySelectorAll('[data-filter-input]').forEach(groupInput => {
          if (groupInput.type === 'radio') {
            groupInput.checked = false;
          }
        });

        // Remove is-active from all items in group
        group.querySelectorAll('[data-filter-item]').forEach(groupItem => {
          groupItem.classList.remove('is-active');
        });

        // If it wasn't active, activate it
        if (!wasActive) {
          input.checked = true;
          item.classList.add('is-active');
        }
      } else if (input.type === 'checkbox') {
        // Checkbox logic: multi-select toggle
        input.checked = !input.checked;
        if (input.checked) {
          item.classList.add('is-active');
        } else {
          item.classList.remove('is-active');
        }
      }

      this.applyFilters();
    });

    // Handle clear filters link
    const clearLink = this.querySelector('[data-clear-filters]');
    if (clearLink) {
      clearLink.addEventListener('click', (e) => {
        e.preventDefault();
        this.clearFilters();
      });
    }

    // Prevent clicks inside filter from closing it
    this.addEventListener('click', (e) => e.stopPropagation());
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    this.isOpen = true;
    this.filterBody.classList.add('is-open');
    this.filterTextClosed.style.display = 'none';
    this.filterTextOpen.style.display = 'block';

    // Close nav menu when opening filter
    const nav = document.querySelector('nav-component');
    if (nav && nav.classList.contains('open')) {
      nav.classList.remove('open');
      document.body.classList.remove('nav-expanded');
    }
  }

  close() {
    this.isOpen = false;
    this.filterBody.classList.remove('is-open');
    this.filterTextClosed.style.display = 'block';
    this.filterTextOpen.style.display = 'none';
  }

  toggleGroup(button) {
    const group = button.closest('[data-filter-group]');
    const items = group.querySelector('[data-filter-items]');
    const icon = button.querySelector('.collection-grid-filter-group-icon');
    const isExpanded = group.classList.contains('is-expanded');

    if (isExpanded) {
      group.classList.remove('is-expanded');
      icon.textContent = '+';
    } else {
      group.classList.add('is-expanded');
      icon.textContent = '-';
    }
  }

  handleClickOutside(e) {
    if (this.isOpen && !this.contains(e.target)) {
      this.close();
    }
  }

  onSubmitForm(e) {
    e.preventDefault();
    this.applyFilters();
  }

  async applyFilters() {
    // If search is active, re-run search with current filters instead
    if (typeof window.collectionSearchIsActive === 'function' && window.collectionSearchIsActive()) {
      const query = window.collectionSearchGetQuery();
      if (query) {
        // Trigger a search re-fetch which will pick up the new filter state
        window.collectionSearchFetch(query);
        return;
      }
    }

    // Cancel any pending request
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    // Build URL with filters - ensure all checked inputs are included
    const checkedInputs = this.querySelectorAll('[data-filter-input]:checked');
    const formData = new FormData(this.filterForm);

    // Double-check that all checked inputs are in the FormData
    checkedInputs.forEach(input => {
      if (!formData.has(input.name)) {
        formData.append(input.name, input.value);
      }
    });

    const searchParams = new URLSearchParams(formData);
    const url = `${window.location.pathname}?section_id=${this.productGrid.dataset.id}&${searchParams.toString()}`;

    // Show loading state
    this.showLoading();

    try {
      const response = await fetch(url, {
        signal: this.abortController.signal
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Update the product grid
      const newGrid = doc.querySelector('#product-grid');
      if (newGrid && this.productGrid) {
        this.productGrid.innerHTML = newGrid.innerHTML;
      }

      // Initialize lazy loading for newly loaded images
      if (typeof window.initLazyImages === 'function') {
        window.initLazyImages();
      }

      // Update the filter form to reflect new counts and active states
      const newFilterBody = doc.querySelector('.collection-grid-filter-body');
      if (newFilterBody) {
        const currentFilterBody = this.filterBody;
        const wasOpen = this.isOpen;

        // Store expanded groups
        const expandedGroups = Array.from(this.querySelectorAll('[data-filter-group].is-expanded')).map(
          group => group.querySelector('[data-filter-toggle] span').textContent.trim()
        );

        this.filterBody.innerHTML = newFilterBody.innerHTML;

        // Update filterForm reference since it was replaced
        this.filterForm = this.querySelector('#FacetFiltersForm');

        // Restore expanded state
        this.querySelectorAll('[data-filter-group]').forEach(group => {
          const label = group.querySelector('[data-filter-toggle] span').textContent.trim();
          if (expandedGroups.includes(label)) {
            group.classList.add('is-expanded');
            group.querySelector('.collection-grid-filter-group-icon').textContent = '-';
          }
        });

        // Restore open state
        if (wasOpen) {
          this.filterBody.classList.add('is-open');
        }

        // Re-initialize event listeners
        this.initFilterEvents();
      }

      // Update URL without page reload
      const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
      window.history.pushState({}, '', searchParams.toString() ? newUrl : window.location.pathname);

      // Hide loading state
      this.hideLoading();

      // Re-apply collection search filter to new grid items
      if (typeof window.collectionSearchReapply === 'function') {
        window.collectionSearchReapply();
      }

    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Filter error:', error);
        this.hideLoading();
      }
    }
  }

  initFilterEvents() {
    // Re-attach filter group toggle events
    this.querySelectorAll('[data-filter-toggle]').forEach(button => {
      button.addEventListener('click', (e) => this.toggleGroup(e.currentTarget));
    });

    // Re-attach clear filters link
    const clearLink = this.querySelector('[data-clear-filters]');
    if (clearLink) {
      clearLink.addEventListener('click', (e) => {
        e.preventDefault();
        this.clearFilters();
      });
    }
  }

  async clearFilters() {
    // Show loading state immediately
    this.showLoading();

    // Build URL without any filters
    const url = `${window.location.pathname}?section_id=${this.productGrid.dataset.id}`;

    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Update the product grid
      const newGrid = doc.querySelector('#product-grid');
      if (newGrid && this.productGrid) {
        this.productGrid.innerHTML = newGrid.innerHTML;
      }

      // Initialize lazy loading for newly loaded images
      if (typeof window.initLazyImages === 'function') {
        window.initLazyImages();
      }

      // Update the filter form
      const newFilterBody = doc.querySelector('.collection-grid-filter-body');
      if (newFilterBody) {
        const wasOpen = this.isOpen;

        // Store expanded groups
        const expandedGroups = Array.from(this.querySelectorAll('[data-filter-group].is-expanded')).map(
          group => group.querySelector('[data-filter-toggle] span').textContent.trim()
        );

        this.filterBody.innerHTML = newFilterBody.innerHTML;

        // Update filterForm reference since it was replaced
        this.filterForm = this.querySelector('#FacetFiltersForm');

        // Restore expanded state
        this.querySelectorAll('[data-filter-group]').forEach(group => {
          const label = group.querySelector('[data-filter-toggle] span').textContent.trim();
          if (expandedGroups.includes(label)) {
            group.classList.add('is-expanded');
            group.querySelector('.collection-grid-filter-group-icon').textContent = '-';
          }
        });

        // Restore open state
        if (wasOpen) {
          this.filterBody.classList.add('is-open');
        }

        // Re-initialize event listeners
        this.initFilterEvents();
      }

      // Update URL without page reload
      window.history.pushState({}, '', window.location.pathname);

      // Hide loading state
      this.hideLoading();

      // Re-apply collection search filter to new grid items
      if (typeof window.collectionSearchReapply === 'function') {
        window.collectionSearchReapply();
      }

    } catch (error) {
      console.error('Clear filters error:', error);
      this.hideLoading();
    }
  }

  showLoading() {
    if (this.filterForm) {
      this.filterForm.style.opacity = '0.7';
      this.filterForm.style.pointerEvents = 'none';
    }
  }

  hideLoading() {
    if (this.filterForm) {
      this.filterForm.style.opacity = '1';
      this.filterForm.style.pointerEvents = 'auto';
    }
  }
}

customElements.define('grid-filter', GridFilter);
